/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { AppConfig } from "../../app";

import { IDataProvider } from "./IDataProvider";

let dataProviderInstance: IDataProvider | null = null;

// Lazy load providers based on env value
const dataProviderPromise = (async (): Promise<IDataProvider> => {
  switch (AppConfig.DataProvider) {
    case "JWPLAYER": {
      const jwModule = await import("./JWPlayer");

      return new jwModule.JWPlayerDataProvider();
    }
    case "AXINOM": {
      const axinomModule = await import("./Axinom");

      return new axinomModule.AxinomDataProvider();
    }
    case "MOTO": {
      const motoModule = await import("./Moto/MotoDataProvider");

      return new motoModule.MotoDataProvider();
    }
    default: {
      const internalModule = await import("./Internal");

      return new internalModule.InternalDataProvider();
    }
  }
})();

// Cache the loaded provider
dataProviderPromise.then((provider) => {
  dataProviderInstance = provider;
});

// Create a proxy that ensures the provider is loaded before any method is called
export const DataProvider: IDataProvider = new Proxy({} as IDataProvider, {
  get: (target, prop) => {
    return async (...args: any[]) => {
      if (!dataProviderInstance) {
        dataProviderInstance = await dataProviderPromise;
      }
      const method = (dataProviderInstance as any)[prop];
      if (typeof method === "function") {
        return method.apply(dataProviderInstance, args);
      }
      return method;
    };
  },
});

// Export async getter for direct access
export const getDataProvider = async (): Promise<IDataProvider> => {
  if (!dataProviderInstance) {
    dataProviderInstance = await dataProviderPromise;
  }
  return dataProviderInstance;
};
